import {
  ChangeDetectionStrategy,
  ApplicationRef,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { Angulartics2GoogleGlobalSiteTag } from 'angulartics2';
import packageInfo from '../../package.json';
import { first, interval, concat, filter } from 'rxjs';
import { SwUpdate } from '@angular/service-worker';
import { environment } from 'src/environments/environment';
import { SubSink } from 'subsink';
import { DeviceDetectionService } from '@core/services/common/device-detection.service';
import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PendingRequestsService } from '@core/services/rest/pending-requests.service';
import { NgxSpinnerModule } from 'ngx-spinner';

declare const gtag: (name: string, id: any, options: any) => void;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, NgxSpinnerModule],
})
export class AppComponent implements OnInit, OnDestroy {
  private angulartics = inject(Angulartics2GoogleGlobalSiteTag);
  private appRef = inject(ApplicationRef);
  private serviceWorkerUdate = inject(SwUpdate);
  private deviceDetectionService = inject(DeviceDetectionService);
  private router = inject(Router);
  private pendingRequestsService = inject(PendingRequestsService);
  private destroyRef = inject(DestroyRef);

  public version = packageInfo.version;
  public isDesktop = this.deviceDetectionService.isDesktop();

  private confirmingVersionChange = false;
  private subs = new SubSink();

  public ngOnInit(): void {
    this.setupAnalitics();
    this.setupChecksForVersionUpdates();
    this.setupHttpRequestCancelation();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private setupChecksForVersionUpdates() {
    if (!this.serviceWorkerUdate.isEnabled) {
      console.log('Service workers are disabled or not supported by this browser');

      return;
    }

    const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable === true));
    this.subs.sink = concat(appIsStable$, interval(60 * 1000)).subscribe(async () => {
      try {
        if (environment.name === 'development') {
          console.log('Service Worker: Checking for new application version.');
        }
        await this.serviceWorkerUdate.checkForUpdate();
      } catch (err) {
        console.error('Service Worker: Failed to check for updates:', err);
      }
    });

    this.subs.sink = this.serviceWorkerUdate.versionUpdates
      .pipe(filter((evt) => evt.type === 'VERSION_READY'))
      .subscribe(() => {
        if (this.confirmingVersionChange) {
          return;
        }
        this.confirmingVersionChange = true;
        const isCurrentRouteKiosk = this.router.url.includes('kiosk');

        const confirmed = confirm(
          'An update to Construct is available. We recommend you load it now for best application performance. Would you like to proceed?',
        );

        if (isCurrentRouteKiosk || confirmed) {
          document.location.reload();
        }
      });

    this.subs.sink = this.serviceWorkerUdate.unrecoverable.subscribe(() => {
      document.location.reload();
    });
  }

  private setupHttpRequestCancelation() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (!this.pendingRequestsService.canBypass()) this.pendingRequestsService.cancelPendingRequests();
      });
  }

  private setupAnalitics() {
    if (environment.googleAnaliticsConfig) {
      gtag('config', environment.googleAnaliticsConfig.measurementId, {});
      this.angulartics.startTracking();
    }
  }
}
